import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { quotesAPI, projectsAPI, catalogAPI } from '../services/api';
import { 
  FileText, 
  Plus, 
  Check,
  X,
  Clock,
  DollarSign,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const Quotes = () => {
  const [searchParams] = useSearchParams();
  const { isDeveloper, isConstructor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [catalog, setCatalog] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchCatalog();
      fetchQuotes();
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const projectsRes = await projectsAPI.getAll();
      const projects = projectsRes?.data?.data || projectsRes?.data || [];
      setProjects(projects);
      if (projects.length > 0 && !selectedProject) {
        setSelectedProject(projects[0].id);
      }
      
      if (isConstructor()) {
        const quotesRes = await quotesAPI.getMyQuotes();
        const quotesData = quotesRes?.data?.data || quotesRes?.data;
        // Asegurar que siempre sea un array
        const quotes = Array.isArray(quotesData) ? quotesData : [];
        setQuotes(quotes);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const response = await catalogAPI.getByProject(selectedProject);
      const catalog = response?.data?.data || response?.data || [];
      setCatalog(catalog);
    } catch (error) {
      console.error('Error fetching catalog:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await quotesAPI.getByProject(selectedProject);
      const quotesData = response?.data?.data || response?.data;
      // Asegurar que siempre sea un array
      const quotes = Array.isArray(quotesData) ? quotesData : [];
      setQuotes(quotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]); // Asegurar que sea array vacío en caso de error
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setSelectedItems(prev => {
      const existing = prev.find(i => i.catalog_item_id === itemId);
      if (existing) {
        return prev.map(i => 
          i.catalog_item_id === itemId 
            ? { ...i, [field]: parseFloat(value) || 0 }
            : i
        );
      } else {
        const catalogItem = catalog.find(c => c.id === itemId);
        return [...prev, {
          catalog_item_id: itemId,
          quantity: field === 'quantity' ? parseFloat(value) || 0 : 0,
          unit_price: field === 'unit_price' ? parseFloat(value) || 0 : catalogItem?.unit_price || 0
        }];
      }
    });
  };

  const removeItem = (itemId) => {
    setSelectedItems(prev => prev.filter(i => i.catalog_item_id !== itemId));
  };

  const handleSubmit = async () => {
    const validItems = selectedItems.filter(i => i.quantity > 0 && i.unit_price > 0);
    
    if (validItems.length === 0) {
      toast.error('Agrega al menos una partida con cantidad y precio');
      return;
    }

    try {
      await quotesAPI.create({
        project_id: selectedProject,
        items: validItems,
        notes
      });
      toast.success('Cotización enviada');
      setDialogOpen(false);
      setSelectedItems([]);
      setNotes('');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al crear cotización');
    }
  };

  const handleAccept = async (quoteId) => {
    try {
      await quotesAPI.accept(quoteId);
      toast.success('Cotización aceptada');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al aceptar cotización');
    }
  };

  const handleReject = async (quoteId) => {
    try {
      await quotesAPI.reject(quoteId);
      toast.success('Cotización rechazada');
      fetchQuotes();
    } catch (error) {
      toast.error('Error al rechazar cotización');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: 'bg-amber-100 text-amber-800',
      aceptada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.pendiente;
  };

  const totalQuote = selectedItems.reduce((sum, item) => 
    sum + (item.quantity * item.unit_price), 0
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Cotizaciones</h1>
            <p className="text-[#5C554B]">
              {isConstructor() ? 'Crea y gestiona tus cotizaciones' : 'Revisa cotizaciones de contratistas'}
            </p>
          </div>
          {isConstructor() && selectedProject && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="new-quote-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Nueva Cotización
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nueva Cotización</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label className="mb-2 block">Seleccionar partidas del catálogo</Label>
                    {catalog.length === 0 ? (
                      <p className="text-sm text-[#5C554B]">No hay partidas disponibles</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {Array.isArray(catalog) && catalog.map((item) => {
                          const selected = selectedItems.find(i => i.catalog_item_id === item.id);
                          return (
                            <div 
                              key={item.id}
                              className={`p-3 rounded-lg border ${selected ? 'border-[#C05621] bg-[#C05621]/5' : 'border-[#E2E8F0]'}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{item.code} - {item.description}</p>
                                  <p className="text-xs text-[#5C554B]">
                                    Disponible: {item.quantity} {item.unit} | P.U. Ref: {formatCurrency(item.unit_price)}
                                  </p>
                                </div>
                                {selected && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => removeItem(item.id)}
                                    className="text-red-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <Input
                                  type="number"
                                  placeholder="Cantidad"
                                  value={selected?.quantity || ''}
                                  onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                                  className="h-9 text-sm"
                                />
                                <Input
                                  type="number"
                                  placeholder="Precio unitario"
                                  value={selected?.unit_price || ''}
                                  onChange={(e) => handleItemChange(item.id, 'unit_price', e.target.value)}
                                  className="h-9 text-sm"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Observaciones o condiciones..."
                      className="mt-1"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <p className="text-sm text-[#5C554B]">Total de la cotización:</p>
                      <p className="text-2xl font-bold text-[#C05621]">{formatCurrency(totalQuote)}</p>
                    </div>
                    <Button onClick={handleSubmit} className="btn-primary" disabled={selectedItems.length === 0}>
                      Enviar Cotización
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Project Selector */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <Label className="mb-2 block">Seleccionar Obra</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-12 bg-white max-w-md">
                <SelectValue placeholder="Selecciona una obra" />
              </SelectTrigger>
              <SelectContent>
                {Array.isArray(projects) && projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Quotes List */}
        {!Array.isArray(quotes) || quotes.length === 0 ? (
          <Card className="card-dashboard">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">No hay cotizaciones</h3>
              <p className="text-[#5C554B]">
                {isConstructor() ? 'Crea tu primera cotización' : 'Aún no hay cotizaciones para esta obra'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Array.isArray(quotes) && quotes.map((quote) => {
              const project = projects.find(p => p.id === quote.project_id);
              return (
                <Card key={quote.id} className="card-dashboard">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-[#C05621]/10 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#C05621]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#2D2A26]">
                              Cotización #{quote.id.slice(0, 8)}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-[#5C554B]">
                              <Building2 className="w-4 h-4" />
                              {project?.name || 'Obra'}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-[#81A183]" />
                            <span className="font-semibold">{formatCurrency(quote.total_amount)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-[#9CA3AF]" />
                            <span className="text-[#5C554B]">
                              {new Date(quote.created_at).toLocaleDateString('es-MX')}
                            </span>
                          </div>
                          <Badge className={getStatusBadge(quote.status)}>
                            {quote.status}
                          </Badge>
                        </div>

                        {quote.notes && (
                          <p className="text-sm text-[#5C554B] mt-2 italic">"{quote.notes}"</p>
                        )}

                        {/* Items preview */}
                        <div className="mt-3 text-xs text-[#5C554B]">
                          {quote.items?.slice(0, 2).map((item, idx) => (
                            <p key={idx}>
                              • {item.catalog_code || 'Partida'}: {item.quantity} x {formatCurrency(item.unit_price)}
                            </p>
                          ))}
                          {quote.items?.length > 2 && (
                            <p>... y {quote.items.length - 2} más</p>
                          )}
                        </div>
                      </div>

                      {isDeveloper() && quote.status === 'pendiente' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAccept(quote.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Aceptar
                          </Button>
                          <Button
                            onClick={() => handleReject(quote.id)}
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Quotes;
